import { useState } from 'react';

export default function InitialAssessment() {
  const [formData, setFormData] = useState({
    incidentNumber: '',
    incidentType: '',
    description: '',
    detectionMethod: '',
    detectionTime: '',
    reportedBy: '',
    initialSeverity: 'medium',
    affectedSystems: '',
    initialContainment: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Sample incident numbers
  const incidentNumbers = [
    '04-01-2024-123456',
    '04-01-2024-123457',
    '04-01-2024-123458',
    '04-01-2024-123459',
  ];

  return (
    <div className="space-y-6 mt-6">
      <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Initial Details</h3>
            <p className="mt-1 text-sm text-gray-500">
              Provide initial information about the incident.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="incidentNumber" className="block text-sm font-medium text-gray-700">
                  Incident Number
                </label>
                <select
                  id="incidentNumber"
                  name="incidentNumber"
                  value={formData.incidentNumber}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">Select incident number...</option>
                  {incidentNumbers.map((number) => (
                    <option key={number} value={number}>{number}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="incidentType" className="block text-sm font-medium text-gray-700">
                  Incident Type
                </label>
                <select
                  id="incidentType"
                  name="incidentType"
                  value={formData.incidentType}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">Select type...</option>
                  <option value="malware">Malware</option>
                  <option value="phishing">Phishing</option>
                  <option value="ddos">DDoS</option>
                  <option value="unauthorized-access">Unauthorized Access</option>
                  <option value="data-breach">Data Breach</option>
                </select>
              </div>

              <div className="col-span-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="detectionMethod" className="block text-sm font-medium text-gray-700">
                  Detection Method
                </label>
                <select
                  id="detectionMethod"
                  name="detectionMethod"
                  value={formData.detectionMethod}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="">Select method...</option>
                  <option value="automated-alert">Automated Alert</option>
                  <option value="manual-detection">Manual Detection</option>
                  <option value="third-party">Third Party Notification</option>
                  <option value="user-report">User Report</option>
                </select>
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="detectionTime" className="block text-sm font-medium text-gray-700">
                  Detection Time
                </label>
                <input
                  type="datetime-local"
                  id="detectionTime"
                  name="detectionTime"
                  value={formData.detectionTime}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="reportedBy" className="block text-sm font-medium text-gray-700">
                  Reported By
                </label>
                <input
                  type="text"
                  id="reportedBy"
                  name="reportedBy"
                  value={formData.reportedBy}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="initialSeverity" className="block text-sm font-medium text-gray-700">
                  Initial Severity
                </label>
                <select
                  id="initialSeverity"
                  name="initialSeverity"
                  value={formData.initialSeverity}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              <div className="col-span-6">
                <label htmlFor="affectedSystems" className="block text-sm font-medium text-gray-700">
                  Affected Systems
                </label>
                <textarea
                  id="affectedSystems"
                  name="affectedSystems"
                  rows={2}
                  value={formData.affectedSystems}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="List affected systems, applications, or services"
                />
              </div>

              <div className="col-span-6">
                <label htmlFor="initialContainment" className="block text-sm font-medium text-gray-700">
                  Initial Containment Actions
                </label>
                <textarea
                  id="initialContainment"
                  name="initialContainment"
                  rows={2}
                  value={formData.initialContainment}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Describe any immediate actions taken to contain the incident"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}